import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Divider,
  ThemeProvider,
  createTheme,
  Toolbar,
  useMediaQuery,
  useTheme
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import PublicHeader from '../components/PublicHeader'; 
import IndustryTagCloud from '../components/IndustryTagCloud';
import AnimatedLineChat from '../components/AnimatedLineChat';
import ContactFormSection from '../components/ContactFormSection';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

// Create custom theme with 16px font size
const customTheme = createTheme({
  typography: {
    body1: { fontSize: '16px' },
    body2: { fontSize: '16px' },
    subtitle1: { fontSize: '16px' },
    button: { fontSize: '16px' },
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        root: { fontSize: '16px', padding: '16px' },
        head: { fontSize: '16px', fontWeight: 'bold' },
        body: { fontSize: '16px' },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { fontSize: '16px' },
      },
    },
  },
});

const FeaturesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navTimeoutRef = useRef(null);
  const { t, i18n } = useTranslation('features');
  const { t: tCommon } = useTranslation('common');
  
  // Get current language
  const currentLang = i18n.language;
  
  // Build industries array with data from translations
  const [industries, setIndustries] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [animationKey, setAnimationKey] = useState(0);

  // Industry configuration with colors and icons
  const industryConfig = {
    business: { color: '#4051B5', icon: '💼' },
    ecommerce: { color: '#E91E63', icon: '🛒' },
    education: { color: '#4CAF50', icon: '📚' },
    insurance: { color: '#FF9800', icon: '🛡️' },
    realestate: { color: '#03A9F4', icon: '🏠' },
    travel: { color: '#9C27B0', icon: '✈️' },
    accounting: { color: '#607D8B', icon: '📊' },
    legal: { color: '#795548', icon: '⚖️' },
    administrative: { color: '#673AB7', icon: '📋' }
  };

  // Extract industry ID from URL query parameters
  const getIndustryFromQuery = () => {
    const params = new URLSearchParams(location.search);
    return params.get('industry');
  };

  // Build industries from translations - updated to properly handle language changes
  useEffect(() => {
    const industryIds = Object.keys(industryConfig);
    const industriesData = industryIds.map(id => ({
      id,
      name: t(`industries.${id}.name`),
      description: t(`industries.${id}.description`),
      color: industryConfig[id].color,
      icon: industryConfig[id].icon,
      chatMessages: t(`industries.${id}.chatMessages`, { returnObjects: true }) || [],
      faqData: t(`industries.${id}.faqData`, { returnObjects: true }) || []
    }));
    
    setIndustries(industriesData);
    
    // Update selected industry when language changes
    if (selectedIndustry) {
      // Find the same industry with updated translations
      const updatedIndustry = industriesData.find(ind => ind.id === selectedIndustry.id);
      if (updatedIndustry) {
        setSelectedIndustry(updatedIndustry);
        // Force animation reset on language change
        setAnimationKey(prev => prev + 1);
      }
    } else {
      // Set initial selected industry
      const industryId = getIndustryFromQuery();
      const initialIndustry = industryId 
        ? industriesData.find(ind => ind.id === industryId) || industriesData[0]
        : industriesData[0];
      setSelectedIndustry(initialIndustry);
    }
  }, [currentLang, t]); // Changed dependency to currentLang and t

  const handleIndustryChange = (industry) => {
    setSelectedIndustry(industry);
    setAnimationKey(prev => prev + 1); // Reset animation when switching industries
  };

  // Dynamic SEO based on language
  const getSeoTitle = () => {
    const baseTitle = t('pageTitle');
    switch(currentLang) {
      case 'ja':
        return `${baseTitle} | LINE AIエージェント - Rebaz AI`;
      case 'en':
        return `${baseTitle} | LINE AI Agent - Rebaz AI`;
      case 'zh-CN':
        return `${baseTitle} | Line AI 代理人 - Rebaz AI`;
      default:
        return `${baseTitle} | Line AI 代理人 - Rebaz AI`;
    }
  };

  const getSeoDescription = () => {
    return t('pageSubtitle');
  };

  const getSeoKeywords = () => {
    switch(currentLang) {
      case 'ja':
        return 'LINE AI, 対話例, カスタマーサービス, AIカスタマーサービス, チャットボット, 業界ソリューション';
      case 'en':
        return 'LINE AI, conversation examples, customer service, AI customer service, chatbot, industry solutions';
      case 'zh-CN':
        return 'LINE AI, 对话范例, 客户服务, AI 客服, 聊天机器人, 行业解决方案';
      default:
        return 'LINE AI, 對話範例, 客戶服務, AI 客服, 聊天機器人, 行業解決方案';
    }
  };

  // Get contact form translations based on language
  const getContactTitle = () => {
    switch(currentLang) {
      case 'ja':
        return '無料相談を予約する';
      case 'en':
        return 'Schedule a Free Consultation';
      case 'zh-CN':
        return '立即免费预约专人咨询';
      default: // zh-TW
        return '立即免費預約專人諮詢';
    }
  };

  const getContactSubtitle = () => {
    switch(currentLang) {
      case 'ja':
        return 'お問い合わせ情報をご記入ください。2営業日以内に専門スタッフがご連絡いたします。';
      case 'en':
        return 'Please leave your information and our consultants will contact you within 2 business days.';
      case 'zh-CN':
        return '请留下信息，我们将有专业顾问于 2 天工作天内与您联系！';
      default: // zh-TW
        return '請留下資訊，我們將有專業顧問於 2 天工作天內與您聯繫！';
    }
  };

  const featuresPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": t('pageTitle'),
    "description": t('pageSubtitle'),
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": industries.map((industry, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": industry.name,
        "description": industry.description
      }))
    }
  };

  const featuresBreadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首頁", "item": "https://rebaz.ai" },
      { "@type": "ListItem", "position": 2, "name": t('pageTitle'), "item": "https://rebaz.ai/features" }
    ]
  };

  // Don't render until industries and selectedIndustry are loaded
  if (!selectedIndustry || industries.length === 0) {
    return null;
  }

  return (
    <>
      <SEO
        title={getSeoTitle()}
        description={getSeoDescription()}
        keywords={getSeoKeywords()}
        canonicalUrl="https://rebaz.ai/features"
        image="/assets/images/section_2_1.png"
        schema={featuresPageSchema}
        breadcrumb={featuresBreadcrumb}
      />
      <ThemeProvider theme={customTheme}>
        <Box sx={{ bgcolor: '#f7f9fc', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <PublicHeader />
          <Toolbar />
          
          {/* Top Title Section */}
          <Box sx={{ 
            bgcolor: '#f7f9fc', 
            py: 4,
            borderBottom: 1,
            borderColor: 'divider'
          }}>
            <Container>
              <Typography 
                variant="h3" 
                component="h1" 
                gutterBottom
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  textAlign: 'center'
                }}
              >
                {t('pageTitle')}
              </Typography>
              <Typography 
                variant="h6" 
                color="text.secondary"
                align="center"
                sx={{ mb: 3, fontSize: '18px' }}
              >
                {t('pageSubtitle')}
              </Typography>
            </Container>
          </Box>

          {/* Industry Tag Cloud Selector */}
          <Container sx={{ mt: 4, mb: 2 }}>
            <IndustryTagCloud 
              industries={industries} 
              selectedIndustry={selectedIndustry} 
              onSelectIndustry={handleIndustryChange} 
            />
          </Container>

          {/* Main Content Area */}
          <Box sx={{ py: 4, flexGrow: 1 }}>
            <Container>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  mb: 3, 
                  fontWeight: 'medium',
                  color: selectedIndustry.color,
                  textAlign: 'center',
                  fontSize: '16px'
                }}
              >
                {selectedIndustry.description}
              </Typography>
              
              <Grid container spacing={4}>
                {/* Left LINE Chat Area */}
                <Grid item xs={12} md={5}>
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      p: 3, 
                      borderRadius: 2,
                      borderTop: 5,
                      borderColor: selectedIndustry.color,
                      height: '100%'
                    }}
                  >
                    <Typography 
                      variant="h5" 
                      component="h2" 
                      gutterBottom
                      sx={{ 
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <Box component="span" sx={{ 
                        fontSize: '24px', 
                        mr: 1,
                        color: selectedIndustry.color
                      }}>
                        {selectedIndustry.icon}
                      </Box>
                      {t(`industries.${selectedIndustry.id}.chatTitle`)}
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    {selectedIndustry.chatMessages && selectedIndustry.chatMessages.length > 0 ? (
                      <AnimatedLineChat key={`${animationKey}-${currentLang}`} messages={selectedIndustry.chatMessages} />
                    ) : (
                      <Box sx={{ 
                        height: 400, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        flexDirection: 'column',
                        bgcolor: '#f9f9f9',
                        borderRadius: 2
                      }}>
                        <Typography color="text.secondary" align="center" sx={{ mb: 2, fontSize: '16px' }}>
                          {t('placeholder.chatInProgress')}
                        </Typography>
                        <Button variant="outlined" sx={{ borderColor: selectedIndustry.color, color: selectedIndustry.color }}>
                          {t('placeholder.addChatExample')}
                        </Button>
                      </Box>
                    )}
                  </Paper>
                </Grid>
                
                {/* Right FAQ Table Area */}
                <Grid item xs={12} md={7}>
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      borderRadius: 2,
                      borderTop: 5,
                      borderColor: selectedIndustry.color,
                      overflow: 'hidden'
                    }}
                  >
                    <Box sx={{ p: 3 }}>
                      <Typography 
                        variant="h5" 
                        component="h2" 
                        gutterBottom
                        sx={{ 
                          fontWeight: 'bold',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <Box component="span" sx={{ 
                          fontSize: '24px', 
                          mr: 1,
                          color: selectedIndustry.color
                        }}>
                          {selectedIndustry.icon}
                        </Box>
                        {t(`industries.${selectedIndustry.id}.faqTitle`)}
                      </Typography>
                      <Typography variant="body2" paragraph color="text.secondary" sx={{ fontSize: '16px' }}>
                        {t(`industries.${selectedIndustry.id}.faqSubtitle`)}
                      </Typography>
                    </Box>
                    
                    {selectedIndustry.faqData && selectedIndustry.faqData.length > 0 ? (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ bgcolor: selectedIndustry.color }}>
                              <TableCell width="5%" sx={{ color: 'white', fontWeight: 'bold', fontSize: '16px' }}>
                                {t('table.numberColumn')}
                              </TableCell>
                              <TableCell width="35%" sx={{ color: 'white', fontWeight: 'bold', fontSize: '16px' }}>
                                {t('table.questionColumn')}
                              </TableCell>
                              <TableCell width="60%" sx={{ color: 'white', fontWeight: 'bold', fontSize: '16px' }}>
                                {t('table.answerColumn')}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedIndustry.faqData.map((row) => (
                              <TableRow key={`${row.id}-${currentLang}`} sx={{ '&:nth-of-type(even)': { bgcolor: '#f7f9fc' } }}>
                                <TableCell sx={{ fontSize: '16px' }}>{row.id}</TableCell>
                                <TableCell sx={{ fontWeight: 'medium', fontSize: '16px' }}>{row.question}</TableCell>
                                <TableCell sx={{ fontSize: '16px' }}>{row.answer}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box sx={{ 
                        p: 3,
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        flexDirection: 'column',
                        bgcolor: '#f9f9f9'
                      }}>
                        <Typography color="text.secondary" align="center" sx={{ mb: 2, fontSize: '16px' }}>
                          {t('placeholder.faqInProgress')}
                        </Typography>
                        <Button variant="outlined" sx={{ borderColor: selectedIndustry.color, color: selectedIndustry.color }}>
                          {t('placeholder.addFaqData')}
                        </Button>
                      </Box>
                    )}
                  </Paper>
                  
                  {/* Knowledge Base Management Card */}
                  <Paper 
                    sx={{ 
                      p: 3, 
                      mt: 4,
                      border: '1px dashed #ccc',
                      borderRadius: 2,
                      bgcolor: '#f8f9fa'
                    }}
                  >
                    <Grid container spacing={3} alignItems="center">
                      <Grid item xs={12} sm={8}>
                        <Typography 
                          variant="subtitle1" 
                          gutterBottom
                          sx={{ fontWeight: 'medium', fontSize: '16px' }}
                        >
                          {t('knowledgeBase.title')}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '16px' }}>
                          {t('knowledgeBase.description')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4} sx={{ textAlign: 'right' }}>
                        <Button 
                          variant="outlined"
                          startIcon={<GetAppIcon />}
                          sx={{ borderColor: selectedIndustry.color, color: selectedIndustry.color }}
                        >
                          {t('knowledgeBase.downloadTemplate')}
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </Container>
          </Box>
          
          {/* Contact Form Section - Replacing Call to Action */}
          <ContactFormSection 
            source="features"
            title={getContactTitle()}
            subtitle={getContactSubtitle()}
          />
          
          <Footer />
        </Box>
      </ThemeProvider>
    </>
  );
};

export default FeaturesPage;